import { type FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSetAtom } from "jotai";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { ToastProvider } from "@stubhub/broadway-web";
import { HeadingLevel, Layout } from "@viagogo/core/ui";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { LocationContext } from "src/shared/context/location-context";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { useHomePageModelData } from "src/website-apps/responsive/stubhub-home/hooks/useHomePageModelData";
import { resolveInitialFavoritesState } from "src/website-apps/responsive/stubhub-home/utils";
import { withDefaultContexts } from "src/website-apps/shared/components/explore/withDefaultContexts";
import { useShowGoogleSignInPrompt } from "src/website-apps/shared/components/google-signin-prompt/hooks/useShowGoogleSignInPrompt";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { ConfirmLocationModal } from "src/website-apps/shared/components/select-location-modal/ConfirmLocationModal";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { setBottomSheetOpenAtom } from "src/website-apps/shared/utils/confirmLocationVisibilityAtom";
import { HomeHeaderLayout } from "./HomeHeaderLayout";
import { HomeLayoutBody } from "./HomeLayoutBody";
import { HomeLayoutFooter } from "./HomeLayoutFooter";

/**
 * The Home App.
 *
 * This components renders `https://www.stubhub.{tld}/` and `https://www.viagogo.{tld}/` pages.
 */
const TLC_LAST_SELECTED_KEY = "tlc_last_selected";

const UnwrappedApp: FC = () => {
  const model = useHomePageModelData();
  const appContext = useAppContext();
  const [userLocation, setUserLocation] = useContext(LocationContext);

  const [mobileMenuOpen, handleOnMobileMenuToggle] = useScrollToggle();

  const hasGenreFilterOptions = model.genreFilterOptions.length > 0;

  const isStickyTlcVariant =
    useSimpleFeatureTernary(FeatureSetEnum.CXWD_3395_StickyTlcSelector, false, true, () => hasGenreFilterOptions) ??
    false;

  const [selectedCarouselGenre, setSelectedCarouselGenre] = useState(() => {
    if (!isStickyTlcVariant) return 0;
    const raw = localStorage.getItem(TLC_LAST_SELECTED_KEY);
    if (!raw) return 0;
    const parsed = Math.trunc(Number(raw));
    return Number.isNaN(parsed) ? 0 : parsed;
  });

  const handleSetSelectedCarouselGenre = useCallback(
    (value: number) => {
      setSelectedCarouselGenre(value);
      if (isStickyTlcVariant) {
        if (value === 0) {
          localStorage.removeItem(TLC_LAST_SELECTED_KEY);
        } else {
          localStorage.setItem(TLC_LAST_SELECTED_KEY, String(value));
        }
      }
    },
    [isStickyTlcVariant],
  );

  const { googleSignInCompleted } = useShowGoogleSignInPrompt({
    featureFlag: FeatureSetEnum.TMHomeAddGoogleSignInPrompt,
    location: "Home",
  });
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const isGoogleGateSatisfied = !isMobile || googleSignInCompleted;
  const setBottomSheetOpen = useSetAtom(setBottomSheetOpenAtom);
  useEffect(() => {
    setBottomSheetOpen({
      id: "home-google-signin-prompt",
      isOpen: !isGoogleGateSatisfied,
    });

    return () => {
      setBottomSheetOpen({
        id: "home-google-signin-prompt",
        isOpen: false,
      });
    };
  }, [isGoogleGateSatisfied, setBottomSheetOpen]);
  const initialFavoritesState = useMemo(() => resolveInitialFavoritesState(model), [model]);

  const Header = useMemo(
    () => (
      <header>
        <HomeHeaderLayout mobileMenuOpen={mobileMenuOpen} model={model} onMobileMenuToggle={handleOnMobileMenuToggle} />
      </header>
    ),
    [model, mobileMenuOpen, handleOnMobileMenuToggle],
  );
  const Body = useMemo(
    () => (
      <HomeLayoutBody
        model={model}
        selectedCarouselGenre={selectedCarouselGenre}
        setSelectedCarouselGenre={handleSetSelectedCarouselGenre}
      />
    ),
    [model, selectedCarouselGenre, handleSetSelectedCarouselGenre],
  );
  const Footer = useMemo(() => <HomeLayoutFooter footerModel={model.footer} />, [model.footer]);

  return (
    <div className={pageWrapperRecipe({ mobileMenu: mobileMenuOpen ? "open" : "closed" })}>
      <FavoritesProvider data={initialFavoritesState}>
        <ToastProvider>
          <HeadingLevel>
            <Layout header={Header} body={Body} footer={Footer} />
            {!appContext.isNativeApp && (
              <ConfirmLocationModal userLocation={userLocation} setUserLocation={setUserLocation} />
            )}
          </HeadingLevel>
        </ToastProvider>
      </FavoritesProvider>
    </div>
  );
};

export const App = withDefaultContexts(UnwrappedApp);
